import {
  MapContainer,
  Marker,
  Popup,
  TileLayer,
  Tooltip,
  useMap,
} from "react-leaflet";
import { Icon, LatLngBounds, LatLngTuple } from "leaflet";
import RedPin from "../../../src/assets/img/avatars/redPin.png";
import BluePin from "../../../src/assets/img/avatars/bluePin.png";
import "leaflet/dist/leaflet.css";
import { useContext, useMemo, useState } from "react";
import { ThemeContext } from "ThemeProvider";
import { useQuery } from "@tanstack/react-query";
import { reqFunction } from "utils/API";
import Loading from "components/loading";
import { Link } from "react-router-dom";

export type counterLocationsInfo = {
  water_meter_location: {
    lat: number | string;
    lng: number | string;
  };
  water_meter_serial: string;
  water_meter_name: string;
  water_meter_project__water_meter_project_name: string;
  water_meter_type__water_meter_tag__water_meter_tag_name: string;
  water_meter_type__water_meter_type_name: string;
};

const FitBoundsToMarkers = ({
  locations,
}: {
  locations: counterLocationsInfo[];
}): JSX.Element | null => {
  const map = useMap();

  useMemo(() => {
    if (!locations.length) return;

    const valid = locations.filter(
      (location) =>
        typeof location.water_meter_location !== "string" &&
        Number(location.water_meter_location.lat) &&
        Number(location.water_meter_location.lng)
    );

    if (!valid.length) return;

    const bounds = new LatLngBounds(
      valid.map((loc) => [
        Number(loc.water_meter_location.lat),
        Number(loc.water_meter_location.lng),
      ])
    );

    map.fitBounds(bounds, { padding: [30, 30] });
  }, [locations, map]);

  return null;
};

const CustomMap = () => {
  const { isDark } = useContext(ThemeContext);

  const customPowerIcon = new Icon({
    iconUrl: RedPin,
    iconSize: [30, 30],
  });

  const customWaterIcon = new Icon({
    iconUrl: BluePin,
    iconSize: [30, 30],
  });

  const defaultCenter: LatLngTuple = [32.10894904606479, 53.493205120165686];

  const {
    data: countersLocation,
    isLoading: locationsLoading,
    status: locationsStatus,
  } = useQuery({
    queryFn: () =>
      reqFunction(
        "watermeters/admin/getlocations",
        {
          type_id_list: null,
          project_id_list: null,
          tag_id_list: null,
        },
        "post"
      ),
    queryKey: ["counterslocation"],
  });

  // All valid locations
  const validLocations: counterLocationsInfo[] = useMemo(
    () =>
      countersLocation?.data?.filter(
        (location: counterLocationsInfo) =>
          typeof location.water_meter_location !== "string" &&
          Number(location.water_meter_location.lat) &&
          Number(location.water_meter_location.lng)
      ) ?? [],
    [countersLocation]
  );

  // Build options from data (tag / project / type)
  const tagOptions = useMemo(
    () =>
      Array.from(
        new Set(
          validLocations
            .map(
              (loc) =>
                loc.water_meter_type__water_meter_tag__water_meter_tag_name
            )
            .filter(Boolean)
        )
      ),
    [validLocations]
  );

  const projectOptions = useMemo(
    () =>
      Array.from(
        new Set(
          validLocations
            .map((loc) => loc.water_meter_project__water_meter_project_name)
            .filter(Boolean)
        )
      ),
    [validLocations]
  );

  const typeOptions = useMemo(
    () =>
      Array.from(
        new Set(
          validLocations
            .map((loc) => loc.water_meter_type__water_meter_type_name)
            .filter(Boolean)
        )
      ),
    [validLocations]
  );

  // Local filter state (string labels)
  const [tagFilter, setTagFilter] = useState<string>("ALL");
  const [projectFilter, setProjectFilter] = useState<string>("ALL");
  const [typeFilter, setTypeFilter] = useState<string>("ALL");

  // Apply frontend filtering
  const filteredLocations = useMemo(
    () =>
      validLocations.filter((loc) => {
        const tagName =
          loc.water_meter_type__water_meter_tag__water_meter_tag_name;
        const projectName = loc.water_meter_project__water_meter_project_name;
        const typeName = loc.water_meter_type__water_meter_type_name;

        const matchTag = tagFilter === "ALL" || tagName === tagFilter;
        const matchProject =
          projectFilter === "ALL" || projectName === projectFilter;
        const matchType = typeFilter === "ALL" || typeName === typeFilter;

        return matchTag && matchProject && matchType;
      }),
    [validLocations, tagFilter, projectFilter, typeFilter]
  );

  const renderPins = (locationsList: counterLocationsInfo[]) => (
    <>
      {locationsList.map((loc, idx) => {
        const lat = Number(loc.water_meter_location.lat);
        const lng = Number(loc.water_meter_location.lng);

        return (
          <Marker
            position={[lat, lng]}
            icon={
              loc.water_meter_type__water_meter_tag__water_meter_tag_name ===
              "آب"
                ? customWaterIcon
                : customPowerIcon
            }
            key={idx + loc.water_meter_serial}
          >
            <Tooltip direction="top" offset={[0, -10]} opacity={0.9}>
              {loc.water_meter_name}
            </Tooltip>

            <Popup>
              <div className="space-y-1 text-xs">
                <div className="text-sm font-bold">{loc.water_meter_name}</div>
                <div className="text-gray-600 dark:text-gray-300">
                  سریال:{" "}
                  <span className="font-mono">{loc.water_meter_serial}</span>
                </div>
                <div className="text-gray-600 dark:text-gray-300">
                  پروژه: {loc.water_meter_project__water_meter_project_name}
                </div>
                <div className="text-gray-600 dark:text-gray-300">
                  نوع: {loc.water_meter_type__water_meter_type_name} (
                  {loc.water_meter_type__water_meter_tag__water_meter_tag_name})
                </div>

                <Link
                  to={`/admin/counters/${loc.water_meter_serial}`}
                  rel="noopener noreferrer"
                  className="mt-1 inline-block text-blue-600 hover:underline dark:text-red-400"
                >
                  مشاهده جزئیات
                </Link>
              </div>
            </Popup>
          </Marker>
        );
      })}
    </>
  );

  return (
    <div
      className={
        isDark ? "dark relative h-full w-full" : "relative h-full w-full"
      }
    >
      {/* Loading state */}
      {locationsLoading && <Loading />}

      {/* Map + overlays */}
      {!locationsLoading && locationsStatus === "success" && (
        <>
          {/* Filter bar overlay */}
          <div className="pointer-events-none absolute inset-x-3 top-3 z-[1000] flex flex-wrap gap-2 rounded-xl bg-white/95 p-2 text-xs shadow-md dark:bg-navy-700/95">
            {/* Tag filter */}
            <div className="pointer-events-auto flex items-center gap-1">
              <span className="text-[11px] text-gray-600 dark:text-gray-200">
                تگ:
              </span>
              <select
                className="rounded-md border border-gray-300 bg-white px-2 py-1 text-[11px] dark:border-navy-500 dark:bg-navy-800"
                value={tagFilter}
                onChange={(e) => setTagFilter(e.target.value)}
              >
                <option value="ALL">همه</option>
                {tagOptions.map((tag) => (
                  <option key={tag} value={tag}>
                    {tag}
                  </option>
                ))}
              </select>
            </div>

            {/* Project filter */}
            <div className="pointer-events-auto flex items-center gap-1">
              <span className="text-[11px] text-gray-600 dark:text-gray-200">
                پروژه:
              </span>
              <select
                className="max-w-[150px] truncate rounded-md border border-gray-300 bg-white px-2 py-1 text-[11px] dark:border-navy-500 dark:bg-navy-800"
                value={projectFilter}
                onChange={(e) => setProjectFilter(e.target.value)}
              >
                <option value="ALL">همه</option>
                {projectOptions.map((proj) => (
                  <option key={proj} value={proj}>
                    {proj}
                  </option>
                ))}
              </select>
            </div>

            {/* Type filter */}
            <div className="pointer-events-auto flex items-center gap-1">
              <span className="text-[11px] text-gray-600 dark:text-gray-200">
                نوع:
              </span>
              <select
                className="max-w-[130px] truncate rounded-md border border-gray-300 bg-white px-2 py-1 text-[11px] dark:border-navy-500 dark:bg-navy-800"
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
              >
                <option value="ALL">همه</option>
                {typeOptions.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>

            {/* Reset button */}
            <button
              type="button"
              className="pointer-events-auto ml-auto rounded-md border border-gray-300 px-2 py-1 text-[11px] hover:bg-gray-50 dark:border-navy-500 dark:hover:bg-navy-600"
              onClick={() => {
                setTagFilter("ALL");
                setProjectFilter("ALL");
                setTypeFilter("ALL");
              }}
            >
              پاک‌سازی فیلترها
            </button>
          </div>

          {/* Legend bottom-right */}
          <div className="pointer-events-none absolute bottom-3 right-3 z-[1000] rounded-xl bg-white/90 p-2 text-xs shadow-md dark:bg-navy-700/90">
            <div className="flex items-center gap-1">
              <span className="h-3 w-3 rounded-full bg-blue-500" />
              <span>کنتور آب</span>
            </div>
            <div className="mt-1 flex items-center gap-1">
              <span className="h-3 w-3 rounded-full bg-red-500" />
              <span>کنتور برق / سایر</span>
            </div>
            <div className="mt-1 text-[10px] text-gray-500 dark:text-gray-300">
              نمایش: {filteredLocations.length} / {validLocations.length}
            </div>
          </div>

          {/* Map itself */}
          <MapContainer
            center={defaultCenter}
            zoom={5}
            className="h-full w-full rounded-xl"
          >
            <TileLayer
              url={
                isDark
                  ? "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
                  : "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              }
            />
            {/* Fit view to filtered markers */}
            <FitBoundsToMarkers locations={filteredLocations} />
            {renderPins(filteredLocations)}
          </MapContainer>
        </>
      )}

      {!locationsLoading && locationsStatus !== "success" && (
        <div className="flex h-full items-center justify-center text-sm text-gray-500 dark:text-gray-200">
          خطا در دریافت موقعیت کنتورها
        </div>
      )}
    </div>
  );
};

export default CustomMap;
